import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type LayoutMode = 'desktop' | 'mobile' | 'native';

interface LayoutContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  isNativeApp: boolean;
  isMobileLayout: boolean;
  isDesktopLayout: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(() => {
    const saved = localStorage.getItem('layoutMode');
    return (saved as LayoutMode) || 'desktop';
  });

  useEffect(() => {
    localStorage.setItem('layoutMode', layoutMode);
  }, [layoutMode]);

  const setLayoutMode = (mode: LayoutMode) => {
    setLayoutModeState(mode);
  };

  const value: LayoutContextType = {
    layoutMode,
    setLayoutMode,
    isNativeApp: layoutMode === 'native',
    isMobileLayout: layoutMode === 'mobile',
    isDesktopLayout: layoutMode === 'desktop',
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}
